/**
 * Billing: what has been charged, and the paperwork for it.
 *
 * This page used to be a single button that sent the traveller to Stripe. That
 * is still the place to change a card or cancel, but "where are my invoices"
 * should not require leaving the app and trusting a redirect — so the documents
 * are listed here, with a download beside each one.
 *
 * Nothing is stored on our side. Each row is read from Stripe when the page
 * opens, and the PDF link is Stripe's own: the invoice of record is the one the
 * payment processor issued, and a copy of ours could disagree with it after a
 * refund or a correction.
 */

import { BillingDocuments } from "@/components/subscription/BillingDocuments";
import { WithdrawalCard } from "@/components/subscription/WithdrawalCard";
import { openCustomerPortal } from "@/services/stripe";
import { useAuthStore } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { CreditCard, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function BillingManagementPage() {
  const { token } = useAuthStore();
  const { currentSubscription, fetchSubscription } = useSubscriptionStore();
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (token) fetchSubscription(token);
  }, [token, fetchSubscription]);

  const handleOpenPortal = async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      await openCustomerPortal(token);
    } catch {
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-500 mt-1">
          Your invoices and receipts, and where to change how you pay
        </p>
      </div>

      {/* The 14-day withdrawal, while it can still be used */}
      {token && (
        <WithdrawalCard onWithdrawn={() => void fetchSubscription(token)} />
      )}

      <BillingDocuments />

      {/* Payment method and subscription */}
      {currentSubscription?.hasStripeCustomer ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <CreditCard className="h-5 w-5 text-gray-500" />
                Payment method and plan
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Change your card, update your billing address, or cancel — at
                Stripe, who hold the card details.
              </p>
            </div>
            <button
              onClick={handleOpenPortal}
              disabled={portalLoading}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              {portalLoading ? "Opening…" : "Open billing portal"}
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center space-y-4">
          <CreditCard className="mx-auto h-12 w-12 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">
            You are on the free plan
          </h2>
          <p className="text-gray-500">
            There is nothing to pay, so there is nothing to manage here yet.
          </p>
          <Link
            to="/traveler/subscription/plans"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700"
          >
            View plans
          </Link>
        </div>
      )}
    </div>
  );
}
